'use client';

import Image from 'next/image';

import { CheckCircle } from 'lucide-react';

import { BentoGrid, type BentoCard } from '@/components/sections/bento-grid';
import { ChallengeVideo } from '@/components/sections/challenge-video';
import { CustomerVideos } from '@/components/sections/customer-videos';
import HeroCentered from '@/components/sections/hero-centered';
import Testimonials from '@/components/sections/testimonials';
import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { useTranslation } from '@/lib/i18n/context';
import { INDUSTRY_FAQS } from '@/lib/industry-faqs';

/* Real tour & activity operators running on Open, rendered with the same
 * mono logo-wall treatment used across the site (see logos.tsx). */
const TOUR_LOGOS = [
  { name: 'RealNZ', src: '/logos/logo-realnz.svg', width: 200, height: 200 },
  {
    name: 'Pacific Whale Foundation',
    src: '/logos/logo-pacwhale.svg',
    width: 311,
    height: 68,
  },
  {
    name: 'Fury Water Adventures',
    src: '/logos/logo-fury.png',
    width: 284,
    height: 178,
  },
  {
    name: 'Futurismo Azores Adventures',
    src: '/logos/logo-futurismo.webp',
    width: 1024,
    height: 246,
  },
  {
    name: 'Jungle Queen Riverboats',
    src: '/logos/logo-junglequeen.png',
    width: 158,
    height: 80,
  },
  {
    name: 'New Orleans Steamboat Co.',
    src: '/logos/logo-neworleans-steamboat.png',
    width: 496,
    height: 166,
  },
  {
    name: 'Eating Europe',
    src: '/logos/logo-eatingeurope.svg',
    width: 160,
    height: 49,
  },
  {
    name: 'Salt River Tubing',
    src: '/logos/logo-saltrivertubing.png',
    width: 375,
    height: 278,
  },
  {
    name: 'Body Glove Ocean Adventures',
    src: '/logos/logo-bodyglove.png',
    width: 3000,
    height: 532,
  },
  {
    name: 'Kona Cloud Forest Sanctuary',
    src: '/logos/logo-konacloudforest.png',
    width: 776,
    height: 604,
  },
  {
    name: 'Incline Railway',
    src: '/logos/logo-inclinerailway.png',
    width: 339,
    height: 248,
  },
  {
    name: 'East Zion Adventures',
    src: '/logos/logo-eastzion.png',
    width: 110,
    height: 125,
  },
  {
    name: 'Paradise Hawaii Tours',
    src: '/logos/logo-paradisehawaii.png',
    width: 188,
    height: 183,
  },
  {
    name: 'Portugal Farm Experiences',
    src: '/logos/logo-portugalfarm.png',
    width: 722,
    height: 165,
  },
  {
    name: 'Aloha Scuba Diving',
    src: '/logos/logo-alohascuba.svg',
    width: 2097,
    height: 2190,
  },
];

/* -------------------------------------------------------------------------- */
/*  Bento grid, tour-operator platform capabilities.                          */
/*  Same treatment as the home page (see platform-bento.tsx): one wide card    */
/*  on top, three vertical cards below, each opening a rich modal.             */
/* -------------------------------------------------------------------------- */

const BENTO_SECTION = {
  en: {
    title: 'Built for how tours actually run',
    subtitle:
      'One AI agent that answers guests, reads your calendar, and takes action, on every channel, in every language, around the clock.',
  },
  ar: {
    title: 'مبني على طريقة عمل الجولات فعلاً',
    subtitle:
      'وكيل ذكاء اصطناعي واحد يجاوب الضيوف، يقرأ تقويمك، ويتصرّف — على كل قناة، بكل لغة، وعلى مدار الساعة.',
  },
} as const;

const BENTO_CARDS_EN: BentoCard[] = [
  {
    id: 'omnichannel',
    title: 'Every guest, every channel, 24/7',
    description:
      'Email, live chat, WhatsApp, and social DMs, Open replies instantly, in the guest’s language, whether it’s midday or 2am.',
    image: 'https://platform.open.cx/changelog/phone-agent-workflows.jpg',
    layout: 'horizontal',
    modal: {
      eyebrow: 'Omnichannel',
      headline: 'One agent wherever guests reach you',
      lead: 'Guests message from a beach, a hotel lobby, or the other side of the planet, Open meets them on every channel with one consistent voice.',
      blocks: [
        {
          type: 'text',
          body: [
            'A huge share of activity bookings land nights and weekends, exactly when your office is dark. A honeymooner in a different timezone wants to add a sunset sail, a family needs to confirm a pickup time before they board a flight, a group of twelve is deciding between Friday and Saturday in a WhatsApp thread. Miss the reply and you miss the booking.',
            'Open answers every message in seconds, on whatever channel the guest chose, and keeps the whole conversation in one place for your team. No copy-pasting between an inbox, a chat widget, and three social apps. One agent, one thread, one source of truth.',
          ],
        },
        {
          type: 'video',
          src: '/home/channels.mp4',
          caption:
            'The same agent, live across chat, WhatsApp, email, and social.',
        },
        {
          type: 'stats',
          items: [
            { value: '24/7', label: 'Always answering' },
            { value: '100+', label: 'Languages' },
            { value: '<10s', label: 'Response time' },
          ],
        },
        {
          type: 'text',
          heading: 'Context that follows the guest',
          body: 'A guest can start in Instagram DMs, switch to WhatsApp to send a passport photo, and finish over email, without ever repeating themselves. The full history, their booking, and every action taken travel with them across the handoff, to the AI and to any human who steps in.',
        },
        {
          type: 'list',
          heading: 'Handled on every channel',
          items: [
            'New bookings and availability checks',
            'Reschedules, date changes, and cancellations',
            'Meeting points, timing, and what to bring',
            'Waivers, accessibility, and group coordination',
            'Refunds applied on your policy',
            'Upsells and add-ons at the right moment',
          ],
        },
        {
          type: 'channels',
          heading: 'Discover our channels',
          subheading:
            'Every channel runs on the same agent. Explore how each one works.',
          items: [
            { name: 'Chat', href: '/channels/chat' },
            { name: 'WhatsApp', href: '/channels/whatsapp' },
            { name: 'Email', href: '/channels/email' },
            { name: 'Social', href: '/channels/social' },
            { name: 'Voice', href: '/channels/voice' },
            { name: 'Slack', href: '/channels/slack' },
          ],
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Which channels are supported?',
              a: 'Email, live chat, web forms, WhatsApp, Instagram, Messenger, and voice, all handled by the same agent, in one place for your team.',
            },
            {
              q: 'Does it really answer around the clock?',
              a: 'Yes. Open responds in seconds no matter the hour, so after-hours and weekend bookings never wait until morning.',
            },
            {
              q: 'Can a conversation move between channels?',
              a: 'Yes. A guest can start in chat, send a document over WhatsApp, and finish over email, the full history and booking context follow them across every handoff.',
            },
            {
              q: 'What happens when a human is needed?',
              a: 'Anything sensitive or unusual is escalated to your team with the full thread and booking context attached, so a person picks up exactly where the guest left off.',
            },
          ],
        },
      ],
      cta: { label: 'See how it works', href: '/demo' },
    },
  },
  {
    id: 'booking-sync',
    title: 'Live booking system sync',
    description:
      'Reads real-time availability, moves and rebooks reservations, and applies your cancellation rules automatically.',
    image: 'https://platform.open.cx/changelog/companion-planning-mode.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Agentic actions',
      headline: 'It doesn’t just chat, it books',
      lead: 'Open connects to your reservation system and finishes the job inside the conversation.',
      blocks: [
        {
          type: 'text',
          body: [
            'Half of your inbox isn’t questions, it’s requests to do something. Move two guests to Friday’s sunrise trip. Add a third kayak. Cancel and refund the rain-out. A deflection to a help article doesn’t resolve any of that. Open does.',
            'It reads live availability, moves and rebooks reservations, applies your cancellation policy, and sends the updated confirmation, all without a human touching the calendar. Then it confirms it back to the guest in plain language.',
          ],
        },
        {
          type: 'video',
          src: '/home/integrations.mp4',
          caption: 'Native connectors to your booking platform and 100+ tools.',
        },
        {
          type: 'stats',
          items: [
            { value: 'Live', label: 'Availability' },
            { value: 'Auto', label: 'Rebooking' },
            { value: '1', label: 'Source of truth' },
          ],
        },
        {
          type: 'list',
          heading: 'Booking actions it takes',
          items: [
            'Check real-time availability across trips and dates',
            'Move, split, or merge reservations',
            'Add guests, gear, and add-ons',
            'Apply cancellation and refund rules',
            'Send updated confirmations automatically',
            'Log every change back to your system',
          ],
        },
        {
          type: 'text',
          heading: 'You stay in control',
          body: 'You decide exactly how much autonomy it has. Low-risk changes run instantly; anything sensitive pauses for a one-click approval from your team, with full context attached. Every action is sandboxed and audit-logged, what ran, on whose approval, and what changed.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Does it connect to my booking software?',
              a: 'Yes. Open integrates with leading tours-and-activities platforms via their APIs, plus any internal system you expose.',
            },
            {
              q: 'Can it actually change reservations?',
              a: 'It takes real actions, moving guests, rebooking, and applying refunds inside the same conversation, then confirming it in plain language.',
            },
            {
              q: 'How do I keep risky actions safe?',
              a: 'Every action runs sandboxed with a full audit trail. High-risk steps pause for a one-click human approval before anything changes.',
            },
          ],
        },
      ],
      cta: { label: 'Explore workflows', href: '/workflows' },
    },
  },
  {
    id: 'weather',
    title: 'Weather-proof rescheduling',
    description:
      'When conditions change, Open notifies guests, offers new dates, and clears the “is it still on?” rush in seconds.',
    image: 'https://platform.open.cx/changelog/versioning-audit.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Disruption handling',
      headline: 'The weather writes your schedule',
      lead: 'Swells, storms, and sunshine reshape the day. Open keeps guests informed and rebooked, politely, and on your policy.',
      blocks: [
        {
          type: 'text',
          body: [
            'A storm rolls in and suddenly every guest booked for tomorrow is messaging the same three words: “is it still on?” On the days you can least afford it, your phone and inbox flood at once. That surge is exactly when guests judge you.',
            'Open gets ahead of it. When a departure is affected, it notifies everyone booked, offers alternative dates, and processes refunds or rebookings automatically, clearing the flood in seconds instead of tying up your crew on the dock.',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: 'Proactive', label: 'Guest alerts' },
            { value: 'Seconds', label: 'To clear the surge' },
            { value: '0', label: 'Manual phone tag' },
          ],
        },
        {
          type: 'list',
          heading: 'When the weather turns',
          items: [
            'Notify every affected guest before they ask',
            'Offer the next available dates automatically',
            'Rebook or refund on your cancellation policy',
            'Keep waitlists and group bookings in sync',
            'Escalate the rare edge case with full context',
          ],
        },
        {
          type: 'text',
          heading: 'On brand, on policy',
          body: 'Every message goes out in your voice and follows your rules, no improvising, no off-policy promises. Guests feel looked after even when the trip can’t sail, and your team stays focused on the guests who are still going out.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'How does it handle weather cancellations?',
              a: 'It notifies guests proactively, offers alternative dates, and processes refunds or rebookings per your policy, clearing the “is it still running?” surge in seconds.',
            },
            {
              q: 'Can it reach everyone at once?',
              a: 'Yes. When a departure is affected, Open messages every booked guest across their channel of choice, all at once.',
            },
          ],
        },
      ],
      cta: { label: 'Talk to us', href: '/demo' },
    },
  },
  {
    id: 'languages',
    title: 'Speaks every guest’s language',
    description:
      'Automatic detection and fluent replies in 100+ languages, no translation delay, no missed details.',
    image: 'https://platform.open.cx/changelog/ai-instructions-ui.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Global by default',
      headline: 'Your manifest is a map of the world',
      lead: 'A traveler from anywhere gets clear answers about meeting points, gear, and timing.',
      blocks: [
        {
          type: 'text',
          body: [
            'Your guests fly in from everywhere, and they message in the language they think in. A visitor asking in Portuguese, Japanese, or German shouldn’t get a slower, clumsier experience than the one asking in English, especially not for the details that decide whether they show up at the right dock, at the right time, with the right gear.',
            'Open detects the guest’s language and replies fluently in over 100 of them, with no translation delay and no misunderstanding of what matters most before a trip.',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '100+', label: 'Languages' },
            { value: 'Auto', label: 'Detection' },
            { value: '0', label: 'Translation delay' },
          ],
        },
        {
          type: 'list',
          heading: 'Clear in any language',
          items: [
            'Meeting points and how to get there',
            'Timing, check-in, and what to bring',
            'Accessibility and fitness requirements',
            'Weather, safety, and cancellation policy',
            'Booking changes and refunds',
          ],
        },
        {
          type: 'text',
          heading: 'One agent, not a phrasebook',
          body: 'You don’t staff a multilingual desk or bolt on a translation plugin that mangles your policy. It’s the same agent, the same knowledge, and the same guardrails, simply understood by every guest who reaches out.',
        },
        {
          type: 'faq',
          heading: 'Frequently asked',
          items: [
            {
              q: 'Can it really answer in my guests’ languages?',
              a: 'Yes, over 100 languages with automatic detection, with fluent, accurate replies about meeting points, gear, and timing.',
            },
            {
              q: 'Do I have to set up each language?',
              a: 'No. Write your knowledge once and Open serves it in the guest’s language automatically, no per-language rebuilds.',
            },
          ],
        },
      ],
      cta: { label: 'See it in action', href: '/demo' },
    },
  },
];

const BENTO_CARDS_AR: BentoCard[] = [
  {
    id: 'omnichannel',
    title: 'كل ضيف، كل قناة، ٢٤/٧',
    description:
      'الإيميل والدردشة وWhatsApp ورسائل السوشيال — Open يرد فوراً بلغة الضيف، وقت الظهر أو الساعة ٢ فجراً.',
    image: 'https://platform.open.cx/changelog/phone-agent-workflows.jpg',
    layout: 'horizontal',
    modal: {
      eyebrow: 'كل القنوات',
      headline: 'وكيل واحد وين ما يجيك الضيف',
      lead: 'الضيوف يراسلونك من شاطئ، من بهو فندق، من الطرف الآخر من العالم. وOpen يقابلهم على كل قناة بصوت واحد ثابت.',
      blocks: [
        {
          type: 'text',
          body: [
            'يأتي جزء كبير من الحجوزات ليلاً وفي نهاية الأسبوع — أي لمّا يكون مكتبك مغلقاً. عروسان في منطقة زمنية أخرى يريدان إضافة رحلة غروب. وعائلة تريد تأكيد وقت الاستقبال قبل صعودها إلى الطائرة. ومجموعة من اثني عشر شخصاً يقرّرون بين الجمعة والسبت في محادثة WhatsApp. يفوتك الرد، يفوتك الحجز.',
            'يرد Open على كل رسالة في ثوانٍ، على أي قناة اختارها الضيف، ويجمع المحادثة كلها في مكان واحد لفريقك. بلا نسخ ولصق بين إيميل وأداة دردشة وثلاثة تطبيقات سوشيال. وكيل واحد، خيط واحد، ومصدر واحد موثوق.',
          ],
        },
        {
          type: 'video',
          src: '/home/channels.mp4',
          caption:
            'الوكيل نفسه، مباشرةً على الدردشة وWhatsApp والإيميل والسوشيال.',
        },
        {
          type: 'stats',
          items: [
            { value: '٢٤/٧', label: 'يرد دائماً' },
            { value: '+١٠٠', label: 'لغة' },
            { value: '<١٠ث', label: 'وقت الرد' },
          ],
        },
        {
          type: 'text',
          heading: 'السياق يتبع الضيف',
          body: 'يبدأ الضيف برسائل Instagram، ثم ينتقل لـ WhatsApp عشان يرسل صورة جواز، ويكمل عبر الإيميل — بدون ما يعيد كلامه ولا مرة. فالمحادثة كاملةً وحجزه وكل إجراء اتُّخذ تنتقل معه في كل تحويل، للـ AI ولأي موظّف يتدخّل.',
        },
        {
          type: 'list',
          heading: 'متكفَّل به على كل قناة',
          items: [
            'الحجوزات الجديدة والتحقق من التوفّر',
            'إعادة الجدولة وتغيير التواريخ والإلغاءات',
            'نقاط اللقاء والتوقيت وما يجب إحضاره',
            'الإقرارات وإمكانية الوصول وتنسيق المجموعات',
            'الاسترداد حسب سياستك',
            'العروض والإضافات في اللحظة المناسبة',
          ],
        },
        {
          type: 'channels',
          heading: 'اكتشف قنواتنا',
          subheading:
            'كل القنوات على الوكيل نفسه. تعرّف على طريقة عمل كل واحدة منها.',
          items: [
            { name: 'Chat', href: '/channels/chat' },
            { name: 'WhatsApp', href: '/channels/whatsapp' },
            { name: 'Email', href: '/channels/email' },
            { name: 'Social', href: '/channels/social' },
            { name: 'Voice', href: '/channels/voice' },
            { name: 'Slack', href: '/channels/slack' },
          ],
        },
        {
          type: 'faq',
          heading: 'أسئلة شائعة',
          items: [
            {
              q: 'وش القنوات المدعومة؟',
              a: 'الإيميل والدردشة المباشرة ونماذج الويب وWhatsApp وInstagram وMessenger والصوت — كلها على الوكيل نفسه، وفي مكان واحد لفريقك.',
            },
            {
              q: 'يرد فعلاً على مدار الساعة؟',
              a: 'نعم. يرد Open في ثوانٍ مهما كان الوقت، فحجوزات ما بعد الدوام ونهاية الأسبوع ما تنتظر الصباح.',
            },
            {
              q: 'هل تنتقل المحادثة بين القنوات؟',
              a: 'نعم. يبدأ الضيف بالدردشة، ويرسل مستنداً عبر WhatsApp، ويكمل عبر الإيميل — وتتبعه المحادثة وسياق الحجز في كل تحويل.',
            },
            {
              q: 'وش يصير لمّا يحتاج الأمر تدخّلاً بشرياً؟',
              a: 'كل أمر حسّاس أو مو اعتيادي يتحوّل لفريقك بكامل الخيط وسياق الحجز، فيكمل الموظّف من النقطة اللي وقف عندها الضيف.',
            },
          ],
        },
      ],
      cta: { label: 'ورّيني كيف يشتغل', href: '/demo' },
    },
  },
  {
    id: 'booking-sync',
    title: 'مزامنة مباشرة مع نظام الحجز',
    description:
      'يقرأ التوفّر لحظياً، ينقل ويعيد الحجوزات، ويطبّق قواعد الإلغاء تلقائياً.',
    image: 'https://platform.open.cx/changelog/companion-planning-mode.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'إجراءات فعلية',
      headline: 'مو بس يتكلم، يحجز',
      lead: 'Open يتصل بنظام الحجوزات ويكمّل المهمة داخل المحادثة.',
      blocks: [
        {
          type: 'text',
          body: [
            'نصف إيميلك مو أسئلة — بل طلبات تنفيذ. انقل ضيفين لرحلة شروق الجمعة. أضف كاياك ثالثاً. ألغِ رحلة المطر وأعد المبلغ. التحويل لمقال مساعدة ما يحل ولا واحدة منها. وOpen يحلها.',
            'يقرأ التوفّر المباشر، وينقل الحجوزات ويعيدها، ويطبّق سياسة الإلغاء، ويرسل التأكيد المحدّث — كل هذا بدون ما يلمس أحد التقويم. ثم يؤكّد للضيف بلغة واضحة.',
          ],
        },
        {
          type: 'video',
          src: '/home/integrations.mp4',
          caption: 'تكاملات أصلية مع منصة الحجز عندك وأكثر من ١٠٠ أداة.',
        },
        {
          type: 'stats',
          items: [
            { value: 'مباشر', label: 'التوفّر' },
            { value: 'تلقائي', label: 'إعادة الحجز' },
            { value: '١', label: 'مصدر موثوق' },
          ],
        },
        {
          type: 'list',
          heading: 'إجراءات الحجز اللي ينفّذها',
          items: [
            'التحقق من التوفّر لحظياً في كل الرحلات والتواريخ',
            'نقل الحجوزات أو تقسيمها أو دمجها',
            'إضافة ضيوف ومعدات وخدمات إضافية',
            'تطبيق قواعد الإلغاء والاسترداد',
            'إرسال تأكيدات محدّثة تلقائياً',
            'تسجيل كل تغيير في نظامك',
          ],
        },
        {
          type: 'text',
          heading: 'تظل أنت المتحكّم',
          body: 'أنت اللي تحدّد حدود صلاحياته. تُنفَّذ التغييرات قليلة المخاطر فوراً، ويتوقّف كل أمر حسّاس لموافقة بضغطة وحدة من فريقك، بكامل السياق. وكل إجراء معزول ومسجّل: وش نُفّذ، وبموافقة مين، ووش تغيّر.',
        },
        {
          type: 'faq',
          heading: 'أسئلة شائعة',
          items: [
            {
              q: 'هل يتصل ببرنامج الحجز عندي؟',
              a: 'نعم. يتكامل مع أبرز منصات حجز الجولات والأنشطة عبر واجهاتها البرمجية، ومع أي نظام داخلي تتيحه.',
            },
            {
              q: 'هل يغيّر الحجوزات فعلاً؟',
              a: 'يتّخذ إجراءات فعلية: ينقل الضيوف، ويعيد الحجز، ويطبّق الاسترداد داخل المحادثة نفسها — ثم يؤكّد بلغة واضحة.',
            },
            {
              q: 'كيف أضمن الإجراءات الحسّاسة؟',
              a: 'كل إجراء يعمل معزولاً مع سجل تدقيق كامل. وتتوقّف الخطوات عالية المخاطر لموافقة بشرية بضغطة قبل أي تغيير.',
            },
          ],
        },
      ],
      cta: { label: 'استكشف سير العمل', href: '/workflows' },
    },
  },
  {
    id: 'weather',
    title: 'إعادة جدولة ما يعطّلها الطقس',
    description:
      'لمّا تتغيّر الظروف، يبلّغ Open الضيوف، يعرض تواريخ جديدة، ويصفّي زحمة «الجولة بعدها قائمة؟» في ثوانٍ.',
    image: 'https://platform.open.cx/changelog/versioning-audit.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'التعامل مع الاضطرابات',
      headline: 'الطقس هو اللي يكتب جدولك',
      lead: 'الأمواج والعواصف والشمس تعيد ترتيب اليوم. وOpen يبقي الضيوف على اطّلاع ويعيد حجزهم — بلباقة، وحسب سياستك.',
      blocks: [
        {
          type: 'text',
          body: [
            'تأتي عاصفة، وفجأة يراسلك كل ضيف محجوز للغد بالكلمات نفسها: «الجولة بعدها قائمة؟». وفي اليوم اللي ما تحتمل فيه ذلك، يطفح هاتفك وإيميلك في اللحظة نفسها. وهذه الموجة بالضبط هي اللي يحكم عليك فيها الضيوف.',
            'وOpen يسبقها. ولمّا تتأثر رحلة، يبلّغ كل المحجوزين، ويعرض تواريخ بديلة، ويعالج الاسترداد أو إعادة الحجز تلقائياً — فيصفّي الموجة في ثوانٍ، بدل ما يقضي فريقك يومه على الرصيف بالهاتف.',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: 'استباقي', label: 'تنبيه الضيوف' },
            { value: 'ثواني', label: 'لتصفية الموجة' },
            { value: '٠', label: 'مكالمات يدوية' },
          ],
        },
        {
          type: 'list',
          heading: 'لمّا يتقلّب الطقس',
          items: [
            'تبليغ كل ضيف متأثّر قبل ما يسأل',
            'عرض أقرب التواريخ المتاحة تلقائياً',
            'إعادة الحجز أو الاسترداد حسب سياستك',
            'إبقاء قوائم الانتظار وحجوزات المجموعات متزامنة',
            'تصعيد الحالة النادرة بكامل السياق',
          ],
        },
        {
          type: 'text',
          heading: 'على هويّتك وحسب سياستك',
          body: 'كل رسالة تخرج بصوتك وتتبع قواعدك — بلا ارتجال، ولا وعود خارج السياسة. يشعر الضيف بالاهتمام حتى لو ما سارت الرحلة، ويظل فريقك مركّزاً على المجموعة التالية.',
        },
        {
          type: 'faq',
          heading: 'أسئلة شائعة',
          items: [
            {
              q: 'كيف يتعامل مع إلغاءات الطقس؟',
              a: 'يبلّغ الضيوف قبل ما يسألون، ويعرض تواريخ بديلة، ويعالج الاسترداد أو إعادة الحجز حسب سياستك — ويصفّي موجة «الجولة بعدها قائمة؟» في ثوانٍ.',
            },
            {
              q: 'هل يوصل للجميع دفعة وحدة؟',
              a: 'نعم. لمّا تتأثر رحلة، يراسل Open كل ضيف محجوز على قناته المفضّلة، في الوقت نفسه.',
            },
          ],
        },
      ],
      cta: { label: 'كلّمنا', href: '/demo' },
    },
  },
  {
    id: 'languages',
    title: 'يتكلّم لغة كل ضيف',
    description:
      'كشف تلقائي وردود بطلاقة بأكثر من ١٠٠ لغة، بلا تأخير ترجمة، ولا تفاصيل ضائعة.',
    image: 'https://platform.open.cx/changelog/ai-instructions-ui.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'عالمي افتراضياً',
      headline: 'قائمة ضيوفك خريطة للعالم',
      lead: 'المسافر من أي مكان يوصله جواب واضح عن نقاط اللقاء والمعدات والتوقيت.',
      blocks: [
        {
          type: 'text',
          body: [
            'يأتي ضيوفك من كل مكان، ويراسلون باللغة اللي يفكّرون بها. والزائر اللي يسأل بالبرتغالية أو اليابانية أو الألمانية ما يستحق تجربة أبطأ وأضعف ممن يسأل بالإنجليزية — خصوصاً في التفاصيل اللي تقرّر وصوله للرصيف الصحيح، في الوقت الصحيح، وبالمعدات الصحيحة.',
            'يكشف Open لغة الضيف ويرد بها بطلاقة، من أكثر من ١٠٠ لغة — بلا تأخير ترجمة، ولا سوء فهم في أهم تفصيل قبل الرحلة.',
          ],
        },
        {
          type: 'stats',
          items: [
            { value: '+١٠٠', label: 'لغة' },
            { value: 'تلقائي', label: 'الكشف' },
            { value: '٠', label: 'تأخير ترجمة' },
          ],
        },
        {
          type: 'list',
          heading: 'واضح بأي لغة',
          items: [
            'نقاط اللقاء وكيف توصلها',
            'التوقيت وتسجيل الوصول وما يجب إحضاره',
            'إمكانية الوصول ومتطلبات اللياقة',
            'الطقس والسلامة وسياسة الإلغاء',
            'تغييرات الحجز والاسترداد',
          ],
        },
        {
          type: 'text',
          heading: 'وكيل واحد، لا كتيّب عبارات',
          body: 'ما تحتاج توظيف مكتب متعدد اللغات، ولا إضافة ترجمة تشوّه سياستك. الوكيل نفسه، والمعرفة نفسها، والضوابط نفسها — بس مفهوم لكل ضيف يتواصل معك.',
        },
        {
          type: 'faq',
          heading: 'أسئلة شائعة',
          items: [
            {
              q: 'يجاوب فعلاً بلغات ضيوفي؟',
              a: 'نعم، أكثر من ١٠٠ لغة مع كشف تلقائي، ورد سليم وواضح عن نقاط اللقاء والمعدات والتوقيت.',
            },
            {
              q: 'هل يلزمني إعداد كل لغة؟',
              a: 'لا. اكتب معرفتك مرة وحدة، ويقدّمها Open بلغة الضيف تلقائياً، بلا إعادة بناء لكل لغة.',
            },
          ],
        },
      ],
      cta: { label: 'شوفه وهو يشتغل', href: '/demo' },
    },
  },
];

const CONTENT = {
  en: {
    eyebrow: 'Industries',
    heroTitleA: 'AI customer support',
    heroTitleB: 'for tour & activity operators',
    heroSubtitle:
      'Late-night bookings, weather questions, group reschedules, Open handles them instantly, in any language, on every channel, so your crew stays in the field.',
    heroCta: 'See how it works for tours',
    inboundTitle: 'Booking change · Sunset Kayak Tour',
    inboundPreview: 'Hi! Can we move our 2 spots to Friday’s sunrise trip?',
    autopilotTitle: 'Autopilot · booking #KY-2291',
    autopilotSteps: [
      'Read message from guest',
      'Checked Friday sunrise availability',
      'Moved 2 guests · Fri 6:00 AM',
      'Sent updated confirmation',
      'Closed ticket · CSAT 5★',
    ],

    logosEyebrow: 'Our Customers',
    logosHeading: 'The operators who show the world a good time run on Open',

    benefitsTitle: 'No two industries book like tours',
    benefitsSubtitle:
      'Experiences are booked on impulse from the other side of the planet. Open is built for how tour and activity businesses actually run.',
    benefits: [
      {
        title: 'Your busiest hours are nobody’s business hours',
        description:
          'Most reservations land nights and weekends, when your office is dark. Open checks availability and confirms bookings 24/7.',
      },
      {
        title: 'The weather writes your schedule',
        description:
          'When conditions change, Open handles cancellations, offers alternative dates, and clears the “is it still on?” flood in seconds, on your policy.',
      },
      {
        title: 'Every guest arrives in a different language',
        description:
          'Open detects and replies in 100+ languages automatically, clear answers on meeting points, gear, and timing for travelers from anywhere.',
      },
      {
        title: 'One connection to your booking system',
        description:
          'Open reads live availability, rebooks reservations, and applies your cancellation rules, then hands rare edge cases to your team with full context.',
      },
      {
        title: 'Everything a guest asks, handled',
        description:
          'Meeting points, what to bring, accessibility, waivers, group coordination, refunds, the everyday questions, answered accurately around the clock.',
        statLabel: 'Guest questions resolved',
        statSublabel: 'With no agent, on autopilot',
      },
    ],

    featuresTitle: 'Everything your guests can ask, handled',
    featuresSubtitle:
      'One agent that covers the full guest journey, before, during, and after the trip.',
    features: [
      {
        title: '24/7 instant replies',
        description:
          'Every message answered in seconds, day or night, across every timezone.',
      },
      {
        title: 'Live availability & booking',
        description:
          'Reads your calendar in real time and confirms open spots on the spot.',
      },
      {
        title: 'Reschedules & date changes',
        description:
          'Moves guests to new dates and updates the reservation automatically.',
      },
      {
        title: 'Weather cancellations',
        description:
          'Notifies affected guests, offers alternatives, and rebooks in seconds.',
      },
      {
        title: 'Refunds on your policy',
        description:
          'Applies your cancellation and refund rules with no back-and-forth.',
      },
      {
        title: '100+ languages',
        description:
          'Detects and replies fluently in the guest’s language, every time.',
      },
      {
        title: 'Group bookings',
        description:
          'Coordinates large parties and last-minute changes in a single thread.',
      },
      {
        title: 'Meeting points & check-in',
        description:
          'Sends directions, timing, and arrival details automatically.',
      },
      {
        title: 'What to bring & waivers',
        description:
          'Answers gear, dress, accessibility, and pre-trip requirements.',
      },
      {
        title: 'Every channel, one place',
        description:
          'Email, live chat, web forms, WhatsApp, and social DMs, unified.',
      },
      {
        title: 'Peak-season scale',
        description:
          'Handles holiday and summer surges with no drop in response time.',
      },
      {
        title: 'Smart escalation',
        description:
          'Hands sensitive cases to your team with the full context attached.',
      },
    ],

    comparison: [
      {
        metric: 'After-hours bookings',
        agent5: 'Answered instantly, 24/7',
        traditional: 'Wait until morning',
      },
      {
        metric: 'Weather cancellations',
        agent5: 'Proactive rebooking in seconds',
        traditional: 'Manual phone tag',
      },
      {
        metric: 'Reschedules & date changes',
        agent5: 'Live availability, auto-updated',
        traditional: 'Back-and-forth email',
      },
      {
        metric: 'Guest languages',
        agent5: '100+, auto-detected',
        traditional: 'English only',
      },
      {
        metric: 'Meeting points & check-in',
        agent5: 'Sent automatically',
        traditional: 'Repeated by hand',
      },
      {
        metric: 'Group bookings',
        agent5: 'Coordinated in one thread',
        traditional: '20-person email chaos',
      },
      {
        metric: 'Refunds',
        agent5: 'Applied by your policy',
        traditional: 'Approval delays',
      },
      {
        metric: 'Peak season',
        agent5: 'Scales instantly',
        traditional: 'Overwhelmed crew',
      },
    ],
    comparisonTitle: 'Why tour operators choose Open',
    comparisonSubtitle:
      'Purpose-built for businesses where the guest experience is everything.',
    colWhatMatters: 'What matters in tours',
    colTraditional: 'Traditional support',

    faqTitle: 'Questions from tour & activity operators',
    faqSubtitle:
      'Straight answers for the businesses that run tours, cruises, rentals, and experiences.',
    faq: INDUSTRY_FAQS['tour-operators'].en,
  },

  ar: {
    eyebrow: 'القطاعات',
    heroTitleA: 'دعم عملاء بالذكاء الاصطناعي',
    heroTitleB: 'لمشغّلي الجولات والأنشطة',
    heroSubtitle:
      'حجز يجي الساعة ٢ فجراً، سؤال «الجولة قائمة مع العاصفة؟»، مجموعة صارت أربعة عشر بدل عشرة. Open يتكفّل فيها فوراً، بأي لغة وعلى كل قناة — وفريقك يظل بالميدان.',
    heroCta: 'ورّيني كيف يشتغل للجولات',
    inboundTitle: 'تغيير حجز · جولة كاياك الغروب',
    inboundPreview: 'هلا! نقدر ننقل مقعدينا لرحلة شروق الجمعة؟',
    autopilotTitle: 'Autopilot · حجز #KY-2291',
    autopilotSteps: [
      'قرأ رسالة الضيف',
      'تحقّق من توفّر شروق الجمعة',
      'نقل ضيفين · الجمعة ٦:٠٠ ص',
      'أرسل تأكيداً محدّثاً',
      'أقفل التذكرة · CSAT 5★',
    ],

    logosEyebrow: 'عملاؤنا',
    logosHeading: 'المشغّلون اللي يصنعون أجمل أيام ضيوفهم يستخدمون Open',

    benefitsTitle: 'ما في قطاع يحجز مثل الجولات',
    benefitsSubtitle:
      'التجارب تُحجز باندفاع من الطرف الآخر للعالم. Open مصمّم لطريقة عمل قطاع الجولات والأنشطة فعلياً.',
    benefits: [
      {
        title: 'ساعات ذروتك مو ساعات دوام أحد',
        description:
          'أغلب الحجوزات تأتي ليلاً ونهاية الأسبوع، لمّا يكون مكتبك مغلقاً. Open يتحقق من التوفّر ويؤكّد الحجوزات ٢٤/٧.',
      },
      {
        title: 'الطقس هو اللي يكتب جدولك',
        description:
          'لمّا تتغيّر الظروف، Open يتكفّل بالإلغاءات، ويعرض تواريخ بديلة، ويصفّي سيل «لا زالت قائمة؟» في ثوانٍ، حسب سياستك.',
      },
      {
        title: 'كل ضيف يأتي بلغة مختلفة',
        description:
          'Open يكشف ويرد بأكثر من ١٠٠ لغة تلقائياً، جواب واضح عن نقاط اللقاء والمعدات والتوقيت للمسافر من أي مكان.',
      },
      {
        title: 'اتصال واحد بنظام الحجز عندك',
        description:
          'Open يقرأ التوفّر المباشر، يعيد الحجوزات، ويطبّق قواعد الإلغاء، وبعدين يحوّل الحالات النادرة لفريقك بكامل السياق.',
      },
      {
        title: 'كل ما يسأله الضيف، متكفَّل به',
        description:
          'نقاط اللقاء، وما يجب إحضاره، وإمكانية الوصول، والإقرارات، وتنسيق المجموعات، والاسترداد — أسئلة كل يوم، يُجاب عليها بدقة على مدار الساعة.',
        statLabel: 'من أسئلة الضيوف تُحل',
        statSublabel: 'بدون موظّف، على Autopilot',
      },
    ],

    featuresTitle: 'كل سؤال يسأله ضيوفك، له جواب',
    featuresSubtitle:
      'وكيل واحد يغطّي رحلة الضيف كاملة — قبلها وأثناءها وبعدها.',
    features: [
      {
        title: 'ردود فورية ٢٤/٧',
        description:
          'كل رسالة تلقى جواباً في ثوانٍ، ليلاً أو نهاراً، وفي أي منطقة زمنية.',
      },
      {
        title: 'توفّر وحجز مباشر',
        description: 'يقرأ تقويمك لحظياً ويؤكّد المقاعد المتاحة في الحال.',
      },
      {
        title: 'إعادة جدولة وتغيير تواريخ',
        description: 'ينقل الضيوف لتواريخ جديدة ويحدّث الحجز تلقائياً.',
      },
      {
        title: 'إلغاءات الطقس',
        description: 'يبلّغ المتأثرين، ويعرض بدائل، ويعيد الحجز في ثوانٍ.',
      },
      {
        title: 'استرداد حسب سياستك',
        description: 'يطبّق قواعد الإلغاء والاسترداد بلا أخذ ورد.',
      },
      {
        title: '+١٠٠ لغة',
        description: 'يكشف ويرد بطلاقة بلغة الضيف في كل مرة.',
      },
      {
        title: 'حجوزات المجموعات',
        description:
          'ينسّق المجموعات الكبيرة وتغييرات آخر لحظة في محادثة وحدة.',
      },
      {
        title: 'نقاط اللقاء وتسجيل الوصول',
        description: 'يرسل الاتجاهات والتوقيت وتفاصيل الوصول تلقائياً.',
      },
      {
        title: 'ما يجب إحضاره والإقرارات',
        description:
          'يجاوب عن المعدات واللبس وإمكانية الوصول وشروط ما قبل الرحلة.',
      },
      {
        title: 'كل القنوات، خيط واحد',
        description:
          'الإيميل والدردشة ونماذج الويب وWhatsApp ورسائل السوشيال — كلها في خيط واحد.',
      },
      {
        title: 'يتوسّع في موسم الذروة',
        description: 'يتحمّل زحمة الإجازات والصيف بدون تباطؤ في وقت الرد.',
      },
      {
        title: 'تصعيد ذكي',
        description: 'يحوّل الحالات الحسّاسة لفريقك بكامل السياق.',
      },
    ],

    comparison: [
      {
        metric: 'الحجوزات بعد الدوام',
        agent5: 'رد فوري ٢٤/٧',
        traditional: 'انتظار الصباح',
      },
      {
        metric: 'إلغاءات الطقس',
        agent5: 'إعادة حجز استباقية في ثوانٍ',
        traditional: 'مكالمات يدوية',
      },
      {
        metric: 'إعادة الجدولة وتغيير التواريخ',
        agent5: 'توفّر مباشر، يتحدّث تلقائياً',
        traditional: 'إيميلات ذهاب وإياب',
      },
      {
        metric: 'لغات الضيوف',
        agent5: '+١٠٠، تُكشف تلقائياً',
        traditional: 'إنجليزي بس',
      },
      {
        metric: 'نقاط اللقاء وتسجيل الوصول',
        agent5: 'تُرسل تلقائياً',
        traditional: 'تتكرر يدوياً',
      },
      {
        metric: 'حجوزات المجموعات',
        agent5: 'تنسيق في محادثة وحدة',
        traditional: 'فوضى إيميل بعشرين شخص',
      },
      {
        metric: 'الاسترداد',
        agent5: 'يُطبّق حسب سياستك',
        traditional: 'تأخير الموافقات',
      },
      {
        metric: 'موسم الذروة',
        agent5: 'يتوسّع فوراً',
        traditional: 'فريق مرهق',
      },
    ],
    comparisonTitle: 'ليش يختار مشغّلو الجولات Open',
    comparisonSubtitle:
      'مصمّم خصيصاً للأعمال اللي تكون تجربة الضيف فيها كل شيء.',
    colWhatMatters: 'اللي يهم في الجولات',
    colTraditional: 'الدعم التقليدي',

    faqTitle: 'أسئلة من مشغّلي الجولات والأنشطة',
    faqSubtitle: 'ردود مباشرة للأعمال اللي تدير جولات وبواخر وتأجير وتجارب.',
    faq: INDUSTRY_FAQS['tour-operators'].ar,
  },
} as const;

export default function TourOperatorsPage() {
  const { locale } = useTranslation();
  const isAr = locale === 'ar';
  const c = isAr ? CONTENT.ar : CONTENT.en;
  const bentoSection = isAr ? BENTO_SECTION.ar : BENTO_SECTION.en;
  const bentoCards = isAr ? BENTO_CARDS_AR : BENTO_CARDS_EN;

  return (
    <div className="bg-[#fafaf9]">
      <HeroCentered
        title={
          <>
            {c.heroTitleA}
            <br />
            {c.heroTitleB}
          </>
        }
        subtitle={c.heroSubtitle}
        ctaText={c.heroCta}
        videoSrc="/home/fareharbor-ai-agent.mp4"
        videoStart={0.1}
      />

      {/* Logo wall, real tour & activity operators, in the site-standard card
          treatment used on the home page (see logos.tsx `cards` variant). */}
      <section className="relative overflow-hidden py-12 md:py-24">
        <div className="container">
          <SubtleEntrance className="mb-16 text-center">
            <p className="mb-3 text-xs font-medium tracking-widest text-black/40 uppercase">
              {c.logosEyebrow}
            </p>
            <h2 className="font-serif text-xl text-black/80 md:text-2xl">
              {c.logosHeading}
            </h2>
          </SubtleEntrance>
          <div className="flex flex-wrap justify-center gap-3">
            {TOUR_LOGOS.map((logo) => (
              <div
                key={logo.name}
                aria-label={logo.name}
                className="group block h-24 w-[calc((100%_-_0.75rem)/2)] sm:w-[calc((100%_-_2.25rem)/4)] lg:w-[calc((100%_-_3.75rem)/6)]"
              >
                <span className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#efece7] px-4 transition-colors group-hover:bg-[#e8e4dd]">
                  <Image
                    src={logo.src}
                    alt={logo.name}
                    width={logo.width}
                    height={logo.height}
                    className="max-h-10 w-auto max-w-[112px] object-contain opacity-70 grayscale transition-all duration-300 ease-out group-hover:opacity-100 group-hover:grayscale-0"
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BentoGrid
        title={bentoSection.title}
        subtitle={bentoSection.subtitle}
        cards={bentoCards}
      />

      {/* Features List, same treatment as the telephony provider pages
          (emerald-check divide-y list of supported capabilities). */}
      <section className="py-16 md:py-32">
        <div className="container">
          <SubtleEntrance className="mb-14 max-w-2xl md:mb-20">
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {c.featuresTitle}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.featuresSubtitle}
            </p>
          </SubtleEntrance>
          <ul className="grid sm:grid-cols-2 sm:gap-x-12">
            {c.features.map((f) => (
              <li
                key={f.title}
                className="flex gap-4 border-b border-neutral-200 py-5"
              >
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div>
                  <h3 className="font-sans text-base text-neutral-900 md:text-lg">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600 md:text-base">
                    {f.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CustomerVideos hideEyebrow />

      {/* $2M guarantee, same section used on the home page */}
      <ChallengeVideo />

      <Testimonials />
    </div>
  );
}
